import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Editor } from "react-draft-wysiwyg";
import {
  ContentState,
  EditorState,
  Modifier,
  RichUtils,
  SelectionState,
  convertFromRaw,
  convertToRaw,
  genKey,
} from "draft-js";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import {
  ArrowsAltOutlined,
  ArrowLeftOutlined,
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  CloseOutlined,
  DeleteOutlined,
  DragOutlined,
  EditOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PictureOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ShrinkOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const FREQUENT_EMOJI_KEY = "linkedin_editor_frequent_emojis";
const ARTICLE_DIVIDER_MARKER = "---";
const ARTICLE_IMAGE_WRITING_PROMPT = "Write your article...";
const isArticleDividerText = (text = "") => /^-{1,3}$/.test(text.trim());

const EMOJI_CATEGORIES = [
  {
    id: "people",
    label: "People",
    icon: "☺",
    emojis: [
      ["😀", "grinning smile happy face"],
      ["😃", "smiley happy face"],
      ["😄", "smile happy face"],
      ["😁", "grin happy face"],
      ["😆", "laugh happy"],
      ["😅", "sweat smile relief"],
      ["😂", "joy laugh tears"],
      ["🤣", "rofl laugh"],
      ["😊", "blush smile"],
      ["😇", "angel innocent"],
      ["🙂", "slight smile"],
      ["🙃", "upside down"],
      ["😉", "wink"],
      ["😍", "heart eyes love"],
      ["🥰", "hearts love"],
      ["😘", "kiss"],
      ["😎", "cool sunglasses"],
      ["🤩", "star struck"],
      ["🤔", "thinking"],
      ["🙄", "rolling eyes"],
      ["😮", "surprised"],
      ["😲", "astonished shocked"],
      ["😳", "flushed embarrassed"],
      ["🥺", "pleading"],
      ["😋", "yum tasty"],
      ["😜", "playful wink"],
      ["🤪", "zany silly"],
      ["🤗", "hug"],
      ["🤭", "oops giggle"],
      ["🫢", "surprised cover mouth"],
      ["🤫", "quiet shush"],
      ["😴", "sleep"],
      ["😢", "cry sad"],
      ["😭", "sob sad"],
      ["😡", "angry"],
      ["🤯", "mind blown"],
      ["🥳", "party face celebrate"],
      ["🫡", "salute"],
    ],
  },
  {
    id: "gestures",
    label: "Gestures",
    icon: "👋",
    emojis: [
      ["👋", "wave hello"],
      ["👍", "thumbs up like"],
      ["👎", "thumbs down"],
      ["👏", "clap applause"],
      ["🙌", "raised hands celebrate"],
      ["🙏", "pray thanks please"],
      ["🤝", "handshake deal"],
      ["💪", "strong muscle"],
      ["👌", "ok perfect"],
      ["✌️", "peace victory"],
      ["🤞", "fingers crossed"],
      ["👉", "point right"],
      ["👇", "point down"],
      ["☝️", "point up"],
      ["✍️", "writing"],
      ["🫶", "heart hands"],
      ["🤙", "call me"],
      ["🖐️", "hand"],
      ["👊", "fist bump"],
      ["🤜", "fist right"],
      ["🤛", "fist left"],
      ["👐", "open hands"],
      ["🙋", "raise hand"],
      ["🙇", "bow thanks"],
    ],
  },
  {
    id: "animals",
    label: "Animals",
    icon: "🐻",
    emojis: [
      ["🐶", "dog"],
      ["🐱", "cat"],
      ["🐻", "bear"],
      ["🐼", "panda"],
      ["🐵", "monkey"],
      ["🦁", "lion"],
      ["🐯", "tiger"],
      ["🦊", "fox"],
      ["🐝", "bee"],
      ["🦋", "butterfly"],
      ["🐢", "turtle"],
      ["🐬", "dolphin"],
      ["🐥", "chick"],
      ["🦅", "eagle"],
      ["🦄", "unicorn"],
      ["🐘", "elephant"],
      ["🦒", "giraffe"],
      ["🐙", "octopus"],
      ["🐠", "fish"],
      ["🌱", "plant growth"],
    ],
  },
  {
    id: "food",
    label: "Food",
    icon: "🍔",
    emojis: [
      ["☕", "coffee"],
      ["🍵", "tea"],
      ["🍕", "pizza"],
      ["🍔", "burger"],
      ["🍟", "fries"],
      ["🌮", "taco"],
      ["🍣", "sushi"],
      ["🍩", "donut"],
      ["🍰", "cake"],
      ["🍎", "apple"],
      ["🍌", "banana"],
      ["🥗", "salad"],
      ["🍪", "cookie"],
      ["🍫", "chocolate"],
      ["🍓", "strawberry"],
      ["🍉", "watermelon"],
      ["🥑", "avocado"],
      ["🍿", "popcorn"],
      ["🍽️", "meal"],
      ["🥂", "cheers"],
    ],
  },
  {
    id: "activity",
    label: "Activity",
    icon: "🎾",
    emojis: [
      ["⚽", "football soccer"],
      ["🏀", "basketball"],
      ["🏏", "cricket"],
      ["🎾", "tennis"],
      ["🏆", "trophy win award"],
      ["🥇", "gold medal"],
      ["🎯", "target goal"],
      ["🎮", "game"],
      ["🎤", "microphone"],
      ["🎧", "headphones"],
      ["🎬", "movie"],
      ["🎨", "art"],
      ["🏃", "run"],
      ["🚴", "cycle"],
      ["🧘", "meditation"],
      ["🎵", "music"],
      ["🎹", "piano"],
      ["📚", "books learning"],
      ["🧩", "puzzle"],
      ["🎁", "gift"],
    ],
  },
  {
    id: "travel",
    label: "Travel",
    icon: "🚗",
    emojis: [
      ["🚗", "car"],
      ["🚕", "taxi"],
      ["🚌", "bus"],
      ["🚆", "train"],
      ["✈️", "plane travel"],
      ["🚀", "rocket launch"],
      ["🏠", "home"],
      ["🏢", "office building"],
      ["🌍", "earth world global"],
      ["📍", "location pin"],
      ["🗓️", "calendar date"],
      ["⏰", "alarm time"],
      ["⏳", "hourglass"],
      ["🌅", "sunrise"],
      ["🌆", "city sunset"],
      ["🏖️", "beach"],
      ["⛰️", "mountain"],
      ["🧭", "compass"],
      ["🛫", "departure"],
      ["🧳", "luggage"],
    ],
  },
  {
    id: "ideas",
    label: "Ideas",
    icon: "💡",
    emojis: [
      ["💡", "idea light"],
      ["🔥", "fire hot"],
      ["✨", "sparkles"],
      ["⭐", "star"],
      ["💫", "dizzy"],
      ["✅", "check done"],
      ["❌", "cross no"],
      ["⚡", "zap energy"],
      ["📌", "pin"],
      ["📣", "announcement"],
      ["💬", "comment chat"],
      ["📈", "growth chart"],
      ["💼", "briefcase work"],
      ["🎉", "party celebrate"],
      ["❤️", "heart love"],
      ["💙", "blue heart"],
      ["💚", "green heart"],
      ["💛", "yellow heart"],
      ["💜", "purple heart"],
      ["🧡", "orange heart"],
      ["🤍", "white heart"],
      ["🚨", "alert"],
      ["🧠", "brain"],
      ["🛠️", "tools"],
      ["🔍", "search"],
      ["📝", "memo note"],
    ],
  },
  {
    id: "symbols",
    label: "Symbols",
    icon: "%",
    emojis: [
      ["💯", "hundred"],
      ["🔔", "bell alert"],
      ["🔒", "lock secure"],
      ["🔑", "key"],
      ["🔗", "link"],
      ["📎", "clip attachment"],
      ["✔️", "check"],
      ["☑️", "checkbox"],
      ["➡️", "arrow right"],
      ["⬅️", "arrow left"],
      ["⬆️", "arrow up"],
      ["⬇️", "arrow down"],
      ["➕", "plus"],
      ["➖", "minus"],
      ["❗", "important"],
      ["❓", "question"],
      ["‼️", "double exclamation"],
      ["⁉️", "question exclamation"],
      ["🔴", "red circle"],
      ["🟢", "green circle"],
      ["🟡", "yellow circle"],
      ["🔵", "blue circle"],
      ["🔼", "up triangle"],
      ["🔽", "down triangle"],
    ],
  },
];

const ALL_EMOJIS = EMOJI_CATEGORIES.flatMap((category) =>
  category.emojis.map(([emoji, keywords]) => ({
    emoji,
    keywords,
    category: category.label,
  }))
);

const getStoredFrequentEmojis = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(FREQUENT_EMOJI_KEY) || "[]");
    return Array.isArray(saved) ? saved.slice(0, 12) : [];
  } catch {
    return [];
  }
};

const hasMeaningfulHtmlContent = (node) =>
  Boolean(
    node?.textContent?.replace(/\u00a0/g, " ").trim() ||
      node?.querySelector?.("img, video, iframe")
  );

const isDividerText = (text = "") =>
  /^-{1,3}$/.test(text.trim()) || /^[\u2013\u2014\u2015_]{2,}$/.test(text.trim());

const hasDividerBorder = (node) =>
  /border-(top|bottom)\s*:/i.test(node?.getAttribute?.("style") || "");

const trimListItemTextNodes = (item) => {
  const walker = item.ownerDocument.createTreeWalker(item, 4);
  const textNodes = [];

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((node) => {
    node.textContent = node.textContent.replace(/[\r\n\t]+/g, " ");
  });

  if (textNodes[0]) {
    textNodes[0].textContent = textNodes[0].textContent.replace(
      /^[\s\u00a0]+/,
      ""
    );
  }

  const lastNode = textNodes[textNodes.length - 1];

  if (lastNode) {
    lastNode.textContent = lastNode.textContent.replace(/[\s\u00a0]+$/, "");
  }
};

const escapeHtml = (text = "") =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapeAttribute = (text = "") => escapeHtml(String(text));

const getBlockImageEntityKey = (block, contentState) => {
  if (!block || block.getType() !== "atomic" || !block.getLength()) {
    return null;
  }

  const entityKey = block.getEntityAt(0);

  if (entityKey === null || entityKey === undefined) {
    return null;
  }

  try {
    return contentState.getEntity(entityKey).getType() === "IMAGE"
      ? entityKey
      : null;
  } catch {
    return null;
  }
};

const getBlockDataObject = (block) => block?.getData?.()?.toObject?.() || {};

const getArticleImageDataForBlock = (block, contentState) => {
  const entityKey = getBlockImageEntityKey(block, contentState);

  if (entityKey === null || entityKey === undefined) {
    return null;
  }

  try {
    return {
      ...contentState.getEntity(entityKey).getData(),
      ...getBlockDataObject(block),
    };
  } catch {
    return null;
  }
};

const getArticleImageStyleVars = (imageData = {}) => ({
  "--article-image-zoom": imageData.zoom ?? 1,
  "--article-image-rotate": `${
    Number(imageData.rotation ?? 0) + Number(imageData.straighten ?? 0)
  }deg`,
  "--article-image-brightness": `${imageData.brightness ?? 100}%`,
  "--article-image-contrast": `${imageData.contrast ?? 100}%`,
  "--article-image-saturation": `${imageData.saturation ?? 100}%`,
  "--article-image-grayscale": `${imageData.grayscale ?? 0}%`,
  "--article-image-sepia": `${imageData.sepia ?? 0}%`,
});

const getArticleImageStyleAttribute = (imageData = {}) =>
  Object.entries(getArticleImageStyleVars(imageData))
    .map(([property, value]) => `${property}: ${value};`)
    .join(" ");

const getLastEditableBlockKey = (contentState) => {
  const blocks = contentState.getBlockMap().toArray();
  const lastEditableBlock = [...blocks]
    .reverse()
    .find((block) => block.getType() !== "atomic");

  return lastEditableBlock?.getKey() || null;
};

const getArticleWritingPromptSelection = (editorState) => {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  const blockKey = selectionState.getStartKey();
  const block = contentState.getBlockForKey(blockKey);

  if (!block || block.getText() !== ARTICLE_IMAGE_WRITING_PROMPT) {
    return null;
  }

  return SelectionState.createEmpty(blockKey).merge({
    anchorOffset: 0,
    focusOffset: block.getLength(),
    hasFocus: true,
    isBackward: false,
  });
};

const replaceArticleWritingPromptText = (editorState, text) => {
  const promptSelection = getArticleWritingPromptSelection(editorState);

  if (!promptSelection) {
    return null;
  }

  const nextContentState = Modifier.replaceText(
    editorState.getCurrentContent(),
    promptSelection,
    text,
    editorState.getCurrentInlineStyle()
  );
  const nextEditorState = EditorState.push(
    editorState,
    nextContentState,
    "insert-characters"
  );

  return EditorState.forceSelection(
    nextEditorState,
    nextContentState.getSelectionAfter()
  );
};

const removeArticleWritingPromptPrefix = (editorState) => {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  const blockKey = selectionState.getStartKey();
  const block = contentState.getBlockForKey(blockKey);
  const blockText = block?.getText?.() || "";

  if (
    !block ||
    blockText === ARTICLE_IMAGE_WRITING_PROMPT ||
    !blockText.startsWith(ARTICLE_IMAGE_WRITING_PROMPT)
  ) {
    return editorState;
  }

  const promptSelection = SelectionState.createEmpty(blockKey).merge({
    anchorOffset: 0,
    focusOffset: ARTICLE_IMAGE_WRITING_PROMPT.length,
    hasFocus: true,
    isBackward: false,
  });
  const nextContentState = Modifier.removeRange(
    contentState,
    promptSelection,
    "forward"
  );
  const nextEditorState = EditorState.push(
    editorState,
    nextContentState,
    "remove-range"
  );

  if (selectionState.getAnchorKey() !== selectionState.getFocusKey()) {
    return EditorState.forceSelection(
      nextEditorState,
      nextContentState.getSelectionAfter()
    );
  }

  return EditorState.forceSelection(
    nextEditorState,
    selectionState.merge({
      anchorOffset: Math.max(
        0,
        selectionState.getAnchorOffset() - ARTICLE_IMAGE_WRITING_PROMPT.length
      ),
      focusOffset: Math.max(
        0,
        selectionState.getFocusOffset() - ARTICLE_IMAGE_WRITING_PROMPT.length
      ),
      hasFocus: true,
    })
  );
};

const convertArticleWritingPromptBlocksToEmpty = (editorState) => {
  const rawContent = convertToRaw(editorState.getCurrentContent());
  let didChange = false;
  const blocks = (rawContent.blocks || []).map((block) => {
    if (block.text !== ARTICLE_IMAGE_WRITING_PROMPT) {
      return block;
    }

    didChange = true;
    return {
      ...block,
      text: "",
      type: "unstyled",
      depth: 0,
      inlineStyleRanges: [],
      entityRanges: [],
      data: {},
    };
  });

  if (!didChange) {
    return editorState;
  }

  const nextContentState = convertFromRaw({
    ...rawContent,
    blocks,
  });

  return EditorState.forceSelection(
    EditorState.createWithContent(nextContentState),
    nextContentState.getSelectionAfter()
  );
};

const removeArticleWritingPromptBlocksFromRaw = (rawContent) => {
  const blocks = (rawContent.blocks || []).filter(
    (block) => block.text !== ARTICLE_IMAGE_WRITING_PROMPT
  );

  return {
    ...rawContent,
    blocks: blocks.length
      ? blocks
      : [
          {
            key: genKey(),
            text: "",
            type: "unstyled",
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [],
            data: {},
          },
        ],
  };
};

const hasArticleImageBlock = (rawContent) =>
  (rawContent.blocks || []).some(
    (block) => block.type === "atomic" && block.entityRanges?.length
  );

const hasArticleBodyText = (rawContent) =>
  (rawContent.blocks || []).some((block) => {
    const text = (block.text || "").trim();

    return text && text !== ARTICLE_IMAGE_WRITING_PROMPT && !isArticleDividerText(text);
  });

const ensureArticleEditableBlockAfterImage = (editorState) => {
  const rawContent = convertToRaw(editorState.getCurrentContent());

  if (!hasArticleImageBlock(rawContent)) {
    return editorState;
  }

  const blocks = rawContent.blocks || [];
  const lastBlock = blocks[blocks.length - 1];

  if (!lastBlock || lastBlock.type !== "atomic") {
    return editorState;
  }

  const writingBlock = {
    key: genKey(),
    text: "",
    type: "unstyled",
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: [],
    data: {},
  };
  const nextContentState = convertFromRaw({
    ...rawContent,
    blocks: [...blocks, writingBlock],
  });
  const nextEditorState = EditorState.createWithContent(nextContentState);

  return EditorState.forceSelection(
    nextEditorState,
    SelectionState.createEmpty(writingBlock.key)
  );
};

const prepareArticleRawContentForHtml = (rawContent) =>
  removeArticleWritingPromptBlocksFromRaw(
    mergeArticleImageBlockDataIntoRaw(rawContent)
  );

const moveRawBlock = (rawContent, sourceKey, targetKey, placement) => {
  if (!sourceKey || !targetKey || sourceKey === targetKey) {
    return null;
  }

  const blocks = rawContent.blocks || [];
  const sourceIndex = blocks.findIndex((block) => block.key === sourceKey);
  const targetIndex = blocks.findIndex((block) => block.key === targetKey);

  if (sourceIndex < 0 || targetIndex < 0) {
    return null;
  }

  const sourceBlock = blocks[sourceIndex];

  if (sourceBlock.type !== "atomic") {
    return null;
  }

  const nextBlocks = blocks.filter((block) => block.key !== sourceKey);
  const nextTargetIndex = nextBlocks.findIndex((block) => block.key === targetKey);

  if (nextTargetIndex < 0) {
    return null;
  }

  nextBlocks.splice(
    placement === "after" ? nextTargetIndex + 1 : nextTargetIndex,
    0,
    sourceBlock
  );

  return {
    ...rawContent,
    blocks: nextBlocks,
  };
};

const articleWritingPromptStrategy = (contentBlock, callback) => {
  if (contentBlock.getText() === ARTICLE_IMAGE_WRITING_PROMPT) {
    callback(0, ARTICLE_IMAGE_WRITING_PROMPT.length);
  }
};

const ArticleWritingPromptSpan = ({ children }) => (
  <span className="linkedin-article-writing-prompt">{children}</span>
);

const articleDecorators = [
  {
    strategy: articleWritingPromptStrategy,
    component: ArticleWritingPromptSpan,
  },
];

const mergeArticleImageBlockDataIntoRaw = (rawContent) => {
  if (!rawContent?.entityMap) {
    return rawContent;
  }

  const entityMap = { ...rawContent.entityMap };

  rawContent.blocks.forEach((block) => {
    if (block.type !== "atomic" || !block.data) {
      return;
    }

    const entityKey = block.entityRanges?.[0]?.key;
    const rawEntityKey =
      entityKey === null || entityKey === undefined ? null : String(entityKey);
    const entity = rawEntityKey ? entityMap[rawEntityKey] : null;

    if (!entity || entity.type !== "IMAGE") {
      return;
    }

    entityMap[rawEntityKey] = {
      ...entity,
      data: {
        ...(entity.data || {}),
        ...block.data,
      },
    };
  });

  return {
    ...rawContent,
    entityMap,
  };
};

const articleEntityTransform = (entity) => {
  if (entity.type !== "IMAGE") {
    return null;
  }

  const sizeClass =
    entity.data?.size === "wide"
      ? "linkedin-article-image-size-wide"
      : "linkedin-article-image-size-normal";
  const ratioClass = `linkedin-article-image-ratio-${
    entity.data?.ratio || "original"
  }`;
  const alignClass = `linkedin-article-image-align-${
    entity.data?.align || "center"
  }`;

  return `<figure class="linkedin-article-image ${sizeClass} ${ratioClass} ${alignClass}"><img src="${escapeAttribute(
    entity.data?.src || ""
  )}" alt="${escapeAttribute(
    entity.data?.alt || "Article image"
  )}" style="${getArticleImageStyleAttribute(entity.data)}" /></figure>`;
};

// Right-to-left scripts (Arabic, Hebrew, Syriac, Thaana, NKo + presentation
// forms) — Persian and Urdu use the Arabic block too.
const RTL_CHAR_PATTERN = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;
const INVISIBLE_ONLY_PATTERN = /^[\s\u00A0\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]*$/;
const RTL_PARAGRAPH_MIN_AVERAGE_LENGTH = 60;
const INVISIBLE_RUN_PATTERN =/[\s\u00A0\u200B-\u200F\u202A-\u202E\u2066-\u2069\uFEFF]+/g;

// Pasted text from a chat window / web page / Word separates paragraphs with
// CSS margin, not a literal blank line, so the plain-text clipboard payload
// can arrive with NO blank lines at all even though the source visibly has
// paragraph spacing \u2014 which collapsed an Arabic post into one tight wall of
// text. The source's own HTML still knows where the paragraphs are, so for
// right-to-left text (scoped on purpose: English/LTR paste keeps its exact
// existing behavior) rebuild the paragraph structure from the HTML blocks,
// guarded by checking that the HTML carries the same text as the plain
// payload. Returns an array of paragraphs, or null to fall back to the
// ordinary line-per-paragraph paste.
const getRtlParagraphsFromHtml = (html, text) => {
  if (!html || typeof DOMParser === "undefined") return null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  const BLOCKS = "p,div,h1,h2,h3,h4,h5,h6,li,blockquote";
  const paragraphs = Array.from(doc.body.querySelectorAll(BLOCKS))
    .filter((el) => !el.querySelector(BLOCKS))
    .map((el) => (el.textContent || "").replace(/\u00A0/g, " ").trim())
    .filter(Boolean);
  if (paragraphs.length < 2) return null;
  const squash = (value) => value.replace(INVISIBLE_RUN_PATTERN, "");
  if (squash(paragraphs.join("")) !== squash(text)) return null;
  return paragraphs;
};

const splitPastedTextIntoLines = (text, html) => {
  const lines = text
    .split(/\r\n?|\n|\u2028|\u2029|\u0085/)
    .map((line) => (INVISIBLE_ONLY_PATTERN.test(line) ? "" : line));
  if (!RTL_CHAR_PATTERN.test(text)) return lines;

  // Only when the paste has no blank line of its own between paragraphs
  // (leading/trailing empties are just the clipboard's edge newlines).
  const first = lines.findIndex((line) => line !== "");
  if (first === -1) return lines;
  let last = lines.length - 1;
  while (lines[last] === "") last -= 1;
  const core = lines.slice(first, last + 1);
  if (core.length < 2 || core.includes("")) return lines;

  const paragraphs = getRtlParagraphsFromHtml(html, text);
  if (paragraphs) {
    return paragraphs.flatMap((paragraph, index) => [
      ...(index ? [""] : []),
      ...paragraph.split("\n"),
    ]);
  }

  // No usable HTML (many sources only put plain text on the clipboard, and
  // some wrap every line in one flat block): the plain text is all we have,
  // and it has one paragraph per line with nothing between them. Prose
  // paragraphs (several sentences each) are almost never meant to be glued
  // together, so give each its own blank line — but only when the lines
  // read as paragraphs (average length), so a deliberately tight list or
  // verse of short lines keeps its exact spacing.
  const averageLength =
    core.reduce((sum, line) => sum + line.trim().length, 0) / core.length;
  if (averageLength < RTL_PARAGRAPH_MIN_AVERAGE_LENGTH) return lines;
  return lines.flatMap((line, index) =>
    index > first && index <= last ? ["", line] : [line]
  );
};

const prepareHtmlForDraft = (html = "") =>
  html
    .replace(/<hr\b[^>]*\/?>\s*(<\/hr>)?/gi, `<p>${ARTICLE_DIVIDER_MARKER}</p>`)
    .replace(/<li\b([^>]*)>\s*<p\b[^>]*>/gi, "<li$1>")
    .replace(/<li\b([^>]*)>\s*<div\b[^>]*>/gi, "<li$1>")
    .replace(/<\/p>\s*<\/li>/gi, "</li>")
    .replace(/<\/div>\s*<\/li>/gi, "</li>")
    .replace(/<li\b[^>]*>\s*(?:<br\s*\/?>|\s|&nbsp;)*<\/li>/gi, "")
    .replace(/<(ul|ol)\b[^>]*>\s*<\/\1>/gi, "");
    // NOTE: a bare "<p></p>" is NOT stripped here (an earlier version of
    // this regex did, and separately an even earlier version also matched
    // <p><br></p>/<p>&nbsp;</p>). All three turned out, at different times,
    // to be exactly how draftToHtml serializes a REAL, intentional blank
    // line: confirmed empirically that a genuinely Enter-typed blank block
    // round-trips as a bare "<p></p>" in this installed version — so there
    // is no content-based way to tell "artifact empty paragraph" apart from
    // "the user's blank line" here, and treating it as an artifact silently
    // deleted every blank line on save/preview. Do not reintroduce this
    // strip without a way to distinguish the two cases.

const normalizeArticlePasteHtml = (html = "") => {
  if (!html || typeof DOMParser === "undefined") {
    return prepareHtmlForDraft(html);
  }

  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(html, "text/html");
  const { body } = parsedDocument;

  body.querySelectorAll("script, style, meta, link").forEach((node) => {
    node.remove();
  });

  body.querySelectorAll("hr").forEach((node) => {
    const divider = parsedDocument.createElement("p");
    divider.textContent = ARTICLE_DIVIDER_MARKER;
    node.replaceWith(divider);
  });

  body.querySelectorAll("p, div").forEach((node) => {
    const isBorderDivider =
      hasDividerBorder(node) && !node.textContent.replace(/\u00a0/g, " ").trim();

    if (!isDividerText(node.textContent) && !isBorderDivider) {
      return;
    }

    const divider = parsedDocument.createElement("p");
    divider.textContent = ARTICLE_DIVIDER_MARKER;
    node.replaceWith(divider);
  });

  body.querySelectorAll("li").forEach((item) => {
    Array.from(item.children).forEach((child) => {
      const tagName = child.tagName?.toLowerCase();

      if (tagName !== "p" && tagName !== "div") {
        return;
      }

      if (!hasMeaningfulHtmlContent(child)) {
        child.remove();
        return;
      }

      while (child.firstChild) {
        item.insertBefore(child.firstChild, child);
      }

      child.remove();
    });

    while (
      item.firstChild &&
      item.firstChild.nodeType === 3 &&
      !item.firstChild.textContent.replace(/\u00a0/g, " ").trim()
    ) {
      item.firstChild.remove();
    }

    while (
      item.firstChild &&
      item.firstChild.nodeType === 1 &&
      item.firstChild.tagName?.toLowerCase() === "br"
    ) {
      item.firstChild.remove();
    }

    if (!hasMeaningfulHtmlContent(item)) {
      item.remove();
      return;
    }

    trimListItemTextNodes(item);
  });

  Array.from(body.children).forEach((node) => {
    const tagName = node.tagName?.toLowerCase();

    if (tagName !== "p" && tagName !== "div") {
      return;
    }

    const text = node.textContent.replace(/\u00a0/g, " ").trim();
    const bulletMatch = text.match(/^(?:[-*•])\s+(.+)$/);
    const orderedMatch = text.match(/^\d+[.)]\s+(.+)$/);
    const listTagName = bulletMatch ? "ul" : orderedMatch ? "ol" : null;
    const listText = bulletMatch?.[1] || orderedMatch?.[1];

    if (!listTagName || !listText) {
      return;
    }

    const previous = node.previousElementSibling;
    const list =
      previous?.tagName?.toLowerCase() === listTagName
        ? previous
        : parsedDocument.createElement(listTagName);
    const item = parsedDocument.createElement("li");

    item.textContent = listText;

    if (list !== previous) {
      node.parentNode.insertBefore(list, node);
    }

    list.appendChild(item);
    node.remove();
  });

  body.querySelectorAll("ul, ol").forEach((list) => {
    if (!list.querySelector("li")) {
      list.remove();
    }
  });

  return prepareHtmlForDraft(body.innerHTML);
};

const textToArticlePasteHtml = (text = "") => {
  const lines = text.split(/\r\n?|\n/);
  const htmlParts = [];
  let listType = null;

  const closeList = () => {
    if (listType) {
      htmlParts.push(`</${listType}>`);
      listType = null;
    }
  };

  lines.forEach((line) => {
    const trimmedLine = line.trim();
    const bulletMatch = trimmedLine.match(/^(?:[-*•])\s+(.+)$/);
    const orderedMatch = trimmedLine.match(/^\d+[.)]\s+(.+)$/);

    if (!trimmedLine) {
      closeList();
      return;
    }

    if (isDividerText(trimmedLine)) {
      closeList();
      htmlParts.push(`<p>${ARTICLE_DIVIDER_MARKER}</p>`);
      return;
    }

    if (bulletMatch) {
      if (listType !== "ul") {
        closeList();
        listType = "ul";
        htmlParts.push("<ul>");
      }

      htmlParts.push(`<li>${escapeHtml(bulletMatch[1])}</li>`);
      return;
    }

    if (orderedMatch) {
      if (listType !== "ol") {
        closeList();
        listType = "ol";
        htmlParts.push("<ol>");
      }

      htmlParts.push(`<li>${escapeHtml(orderedMatch[1])}</li>`);
      return;
    }

    closeList();
    htmlParts.push(`<p>${escapeHtml(trimmedLine)}</p>`);
  });

  closeList();
  return htmlParts.join("");
};

const serializeEditorHtml = (html = "") =>
  html
    .replace(/<(p|div)>\s*-{1,3}\s*<\/\1>/gi, "<hr />")
    .replace(/<li\b([^>]*)>\s*<p\b[^>]*>/gi, "<li$1>")
    .replace(/<li\b([^>]*)>\s*<div\b[^>]*>/gi, "<li$1>")
    .replace(/<\/p>\s*<\/li>/gi, "</li>")
    .replace(/<\/div>\s*<\/li>/gi, "</li>")
    .replace(/<li\b[^>]*>\s*(?:<br\s*\/?>|\s|&nbsp;)*<\/li>/gi, "")
    .replace(/<(ul|ol)\b[^>]*>\s*<\/\1>/gi, "");
    // NOTE: a bare "<p></p>" is NOT stripped here. Confirmed empirically
    // that draftToHtml serializes a genuinely Enter-typed blank block as
    // exactly that — a bare "<p></p>", not "<p><br></p>" or "<p>&nbsp;</p>"
    // as earlier versions of this comment assumed — so there is no
    // content-based way to tell "artifact empty paragraph" apart from "the
    // user's real blank line" here. Stripping it deleted every blank line
    // on save/preview (this function's output feeds the LinkedIn preview
    // panel too, not just the editor). Do not reintroduce this strip
    // without a real way to distinguish the two cases.

const hasEntityKey = (entityMap, key) =>
  key !== null &&
  key !== undefined &&
  key !== "null" &&
  Object.prototype.hasOwnProperty.call(entityMap || {}, String(key));

const sanitizeArticleEditorState = (editorState) => {
  const rawContent = convertToRaw(editorState.getCurrentContent());
  const entityMap = rawContent.entityMap || {};
  let didChange = false;
  const blocks = rawContent.blocks
    .map((block) => {
      const entityRanges = (block.entityRanges || []).filter((range) =>
        hasEntityKey(entityMap, range?.key)
      );

      if (entityRanges.length !== (block.entityRanges || []).length) {
        didChange = true;
      }

      if (block.type === "atomic") {
        const imageEntity = entityRanges
          .map((range) => entityMap[String(range.key)])
          .find((entity) => entity?.type === "IMAGE");

        if (!imageEntity) {
          didChange = true;
          return null;
        }
      }

      return entityRanges === block.entityRanges
        ? block
        : {
            ...block,
            entityRanges,
          };
    })
    .filter(Boolean);

  if (!didChange) {
    return editorState;
  }

  if (!blocks.length) {
    return EditorState.createEmpty();
  }

  return EditorState.createWithContent(
    convertFromRaw({
      ...rawContent,
      blocks,
    })
  );
};

const createEditorStateFromHtml = (content = "", sanitizeArticle = false) => {
  if (!content) {
    return EditorState.createEmpty();
  }

  const contentBlock = htmlToDraft(prepareHtmlForDraft(content));

  if (!contentBlock) {
    return EditorState.createEmpty();
  }

  const contentState = ContentState.createFromBlockArray(
    contentBlock.contentBlocks,
    contentBlock.entityMap
  );

  const nextEditorState = EditorState.createWithContent(contentState);

  if (!sanitizeArticle) {
    return nextEditorState;
  }

  return ensureArticleEditableBlockAfterImage(
    convertArticleWritingPromptBlocksToEmpty(
      sanitizeArticleEditorState(nextEditorState)
    )
  );
};

export const normalizeEditorHtml = (html = "") =>
  serializeEditorHtml(
    html
      .replace(/&nbsp;/g, " ")
      // Only trims trailing whitespace that follows REAL content (a
      // non-whitespace, non-">" character) — a paragraph whose whole body
      // is whitespace (e.g. "<p> </p>", draftToHtml's own encoding of a
      // blank line once &nbsp; above becomes a space) must reach
      // serializeEditorHtml's empty-paragraph check untouched. The
      // previous unconditional "\s+</p>" match also fired right after the
      // opening "<p>" tag's own ">" character, quietly erasing every
      // blank line's only content before it could be recognized as one.
      .replace(/([^>\s])\s+<\/p>/g, "$1</p>")
      .trim()
  );

const isEditorStateEmpty = (editorState) => {
  const contentState = editorState.getCurrentContent();
  const plainText = contentState.getPlainText().trim();
  const rawContent = convertToRaw(contentState);
  const hasEntityContent = Object.keys(rawContent.entityMap || {}).length > 0;

  if (hasEntityContent) {
    return false;
  }

  return (
    !plainText &&
    contentState.getBlockMap().every((block) => !block.getText().trim())
  );
};

const toolbarByPreset = {
  basic: {
    options: ["inline", "list", "textAlign"],
    inline: {
      options: ["bold", "italic", "underline"],
    },
    list: {
      options: ["unordered"],
    },
  },
  // A lightweight per-section editor for structured, document-style forms
  // (e.g. the Blog Write outline builder) — same inline formatting as
  // "basic" plus underline and a real image button (react-draft-wysiwyg's
  // own built-in upload/drag-drop, wired via the imageUploadCallback prop
  // below — NOT the "article" preset's LinkedIn-post-styled cover/byline
  // chrome, which would look wrong repeated many times in one form).
  outline: {
    options: ["inline", "list", "textAlign", "image"],
    inline: {
      options: ["bold", "italic", "underline"],
    },
    list: {
      options: ["unordered"],
    },
    image: {
      previewImage: true,
      inputAccept: "image/*",
      alt: { present: false, mandatory: false },
      uploadEnabled: true,
    },
  },
  linkedin: {
    options: ["inline", "history", "list"],
    inline: {
      inDropdown: false,
      options: ["bold", "italic", "strikethrough"],
    },
    history: {
      options: ["undo", "redo"],
    },
    list: {
      inDropdown: false,
      options: ["unordered", "ordered", "indent", "outdent"],
    },
  },
  article: {
    options: [],
  },
};

const ARTICLE_BLOCK_OPTIONS = [
  { label: "Style", value: "unstyled" },
  { label: "Heading 1", value: "header-one" },
  { label: "Heading 2", value: "header-two" },
  { label: "Heading 3", value: "header-three" },
];

const ArticleToolbarButton = ({ active, children, label, onClick }) => (
  <button
    type="button"
    className={`linkedin-article-toolbar-button${active ? " active" : ""}`}
    title={label}
    aria-label={label}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
  >
    {children}
  </button>
);

const ArticleDividerBlock = ({ block }) => (
  <div className="linkedin-article-divider-block">
    <span className="linkedin-article-divider-line" role="separator" />
    <button
      type="button"
      className="linkedin-article-divider-delete"
      data-divider-delete-key={block.getKey()}
      title="Delete divider"
      aria-label="Delete divider"
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <DeleteOutlined />
    </button>
  </div>
);

const ArticleImageBlock = ({ block, contentState }) => {
  const imageData = getArticleImageDataForBlock(block, contentState);

  if (!imageData?.src) {
    return <div className="linkedin-article-empty-atomic" />;
  }

  const blockKey = block.getKey();
  const isWide = imageData.size === "wide";
  const imageAlign = imageData.align || "center";
  const imageStyle = getArticleImageStyleVars(imageData);

  return (
    <figure
      contentEditable={false}
      draggable
      data-article-image-key={blockKey}
      data-article-image-draggable="true"
      className={`linkedin-article-image-block linkedin-article-image-align-${imageAlign}${
        isWide ? " linkedin-article-image-block-wide" : ""
      } linkedin-article-image-ratio-${imageData.ratio || "original"}`}
    >
      <div className="linkedin-article-image-frame">
        <img
          src={imageData.src}
          alt={imageData.alt || "Article image"}
          style={imageStyle}
        />
        <div className="linkedin-article-image-actions">
          <button
            type="button"
            className="linkedin-article-image-drag"
            data-article-image-key={blockKey}
            title="Drag image"
            aria-label="Drag image"
          >
            <DragOutlined />
          </button>
          <button
            type="button"
            data-article-image-action="align-left"
            data-article-image-key={blockKey}
            title="Wrap left"
            aria-label="Wrap left"
          >
            <AlignLeftOutlined />
          </button>
          <button
            type="button"
            data-article-image-action="align-center"
            data-article-image-key={blockKey}
            title="Center image"
            aria-label="Center image"
          >
            <AlignCenterOutlined />
          </button>
          <button
            type="button"
            data-article-image-action="align-right"
            data-article-image-key={blockKey}
            title="Wrap right"
            aria-label="Wrap right"
          >
            <AlignRightOutlined />
          </button>
          <button
            type="button"
            data-article-image-action="toggle"
            data-article-image-key={blockKey}
            title={isWide ? "Minimize image" : "Maximize image"}
            aria-label={isWide ? "Minimize image" : "Maximize image"}
          >
            {isWide ? <ShrinkOutlined /> : <ArrowsAltOutlined />}
          </button>
          <button
            type="button"
            data-article-image-action="edit"
            data-article-image-key={blockKey}
            title="Edit image"
            aria-label="Edit image"
          >
            <EditOutlined />
          </button>
          <button
            type="button"
            data-article-image-action="delete"
            data-article-image-key={blockKey}
            title="Delete image"
            aria-label="Delete image"
          >
            <DeleteOutlined />
          </button>
        </div>
      </div>
    </figure>
  );
};

const ARTICLE_IMAGE_RATIOS = [
  { label: "Original", value: "original" },
  { label: "Square", value: "square" },
  { label: "4:1", value: "4-1" },
  { label: "3:4", value: "3-4" },
  { label: "16:9", value: "16-9" },
];

const ARTICLE_IMAGE_FILTERS = [
  {
    label: "Original",
    value: "original",
    values: {
      brightness: 100,
      contrast: 100,
      saturation: 100,
      grayscale: 0,
      sepia: 0,
    },
  },
  {
    label: "Vivid",
    value: "vivid",
    values: {
      brightness: 105,
      contrast: 112,
      saturation: 132,
      grayscale: 0,
      sepia: 0,
    },
  },
  {
    label: "Warm",
    value: "warm",
    values: {
      brightness: 104,
      contrast: 105,
      saturation: 112,
      grayscale: 0,
      sepia: 18,
    },
  },
  {
    label: "Cool",
    value: "cool",
    values: {
      brightness: 102,
      contrast: 104,
      saturation: 92,
      grayscale: 0,
      sepia: 0,
    },
  },
  {
    label: "Mono",
    value: "mono",
    values: {
      brightness: 100,
      contrast: 108,
      saturation: 100,
      grayscale: 100,
      sepia: 0,
    },
  },
];

const ArticleImageEditorModal = ({
  draft,
  imageUrl,
  onApply,
  onChange,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState("crop");

  if (!draft) {
    return null;
  }

  const currentFilter =
    ARTICLE_IMAGE_FILTERS.find((filter) => filter.value === draft.filter) ||
    ARTICLE_IMAGE_FILTERS[0];
  const getAdjustValue = (key) =>
    draft[key] ?? (["brightness", "contrast", "saturation"].includes(key) ? 100 : 0);

  return (
    <div className="linkedin-image-edit-overlay" role="dialog" aria-modal="true">
      <div className="linkedin-image-edit-modal">
        <div className="linkedin-image-edit-header">
          <strong>Add image or video</strong>
          <button
            type="button"
            aria-label="Close editor"
            onClick={onClose}
          >
            <CloseOutlined />
          </button>
        </div>
        <div className="linkedin-image-edit-body">
          <div
            className={`linkedin-image-edit-preview linkedin-image-edit-preview-${draft.ratio}`}
          >
            <img
              src={imageUrl}
              alt="Selected article media"
              style={getArticleImageStyleVars(draft)}
            />
          </div>
          <aside className="linkedin-image-edit-panel">
            <div className="linkedin-image-edit-panel-head">
              <button type="button" aria-label="Back" onClick={onClose}>
                <ArrowLeftOutlined />
              </button>
              <span>Edit</span>
            </div>
            <div className="linkedin-image-edit-tabs">
              <button
                type="button"
                className={activeTab === "crop" ? "active" : ""}
                onClick={() => setActiveTab("crop")}
              >
                Crop
              </button>
              <button
                type="button"
                className={activeTab === "filter" ? "active" : ""}
                onClick={() => setActiveTab("filter")}
              >
                Filter
              </button>
              <button
                type="button"
                className={activeTab === "adjust" ? "active" : ""}
                onClick={() => setActiveTab("adjust")}
              >
                Adjust
              </button>
            </div>

            {activeTab === "crop" ? (
              <>
                <div className="linkedin-image-edit-tools">
                  <button
                    type="button"
                    aria-label="Rotate left"
                    onClick={() =>
                      onChange({
                        rotation: (Number(draft.rotation || 0) - 90) % 360,
                      })
                    }
                  >
                    <RotateLeftOutlined />
                  </button>
                  <button
                    type="button"
                    aria-label="Rotate right"
                    onClick={() =>
                      onChange({
                        rotation: (Number(draft.rotation || 0) + 90) % 360,
                      })
                    }
                  >
                    <RotateRightOutlined />
                  </button>
                </div>
                <div className="linkedin-image-edit-section">
                  <p>Aspect Ratio</p>
                  <div className="linkedin-image-ratio-options">
                    {ARTICLE_IMAGE_RATIOS.map((ratio) => (
                      <button
                        type="button"
                        key={ratio.value}
                        className={draft.ratio === ratio.value ? "active" : ""}
                        onClick={() => onChange({ ratio: ratio.value })}
                      >
                        {ratio.label}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="linkedin-image-edit-range">
                  <span>Zoom</span>
                  <input
                    type="range"
                    min="1"
                    max="2"
                    step="0.05"
                    value={draft.zoom}
                    onChange={(event) =>
                      onChange({ zoom: Number(event.target.value) })
                    }
                  />
                </label>
                <label className="linkedin-image-edit-range">
                  <span>Straighten</span>
                  <input
                    type="range"
                    min="-20"
                    max="20"
                    step="1"
                    value={draft.straighten ?? 0}
                    onChange={(event) =>
                      onChange({ straighten: Number(event.target.value) })
                    }
                  />
                </label>
              </>
            ) : null}

            {activeTab === "filter" ? (
              <div className="linkedin-image-filter-grid">
                {ARTICLE_IMAGE_FILTERS.map((filter) => (
                  <button
                    type="button"
                    key={filter.value}
                    className={
                      currentFilter.value === filter.value ? "active" : ""
                    }
                    onClick={() =>
                      onChange({ filter: filter.value, ...filter.values })
                    }
                  >
                    <span
                      className="linkedin-image-filter-thumb"
                      style={getArticleImageStyleVars(filter.values)}
                    >
                      <img src={imageUrl} alt="" />
                    </span>
                    <span>{filter.label}</span>
                  </button>
                ))}
              </div>
            ) : null}

            {activeTab === "adjust" ? (
              <>
                {[
                  ["brightness", "Brightness", 50, 150],
                  ["contrast", "Contrast", 50, 150],
                  ["saturation", "Saturation", 0, 200],
                  ["grayscale", "Fade", 0, 100],
                  ["sepia", "Warmth", 0, 100],
                ].map(([key, label, min, max]) => (
                  <label className="linkedin-image-edit-range" key={key}>
                    <span>
                      {label}
                      <em>{getAdjustValue(key)}</em>
                    </span>
                    <input
                      type="range"
                      min={min}
                      max={max}
                      step="1"
                      value={getAdjustValue(key)}
                      onChange={(event) =>
                        onChange({
                          filter: "custom",
                          [key]: Number(event.target.value),
                        })
                      }
                    />
                  </label>
                ))}
              </>
            ) : null}

            <button
              type="button"
              className="linkedin-image-edit-apply"
              onClick={onApply}
            >
              Apply
            </button>
          </aside>
        </div>
      </div>
    </div>
  );
};

const EmojiToolbarButton = ({ editorState, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("people");
  const [searchTerm, setSearchTerm] = useState("");
  const [frequentEmojis, setFrequentEmojis] = useState(getStoredFrequentEmojis);

  const insertEmoji = (emoji) => {
    const promptReplacementEditorState = replaceArticleWritingPromptText(
      editorState,
      emoji
    );
    const nextFrequentEmojis = [
      emoji,
      ...frequentEmojis.filter((item) => item !== emoji),
    ].slice(0, 12);

    if (promptReplacementEditorState) {
      setFrequentEmojis(nextFrequentEmojis);
      localStorage.setItem(
        FREQUENT_EMOJI_KEY,
        JSON.stringify(nextFrequentEmojis)
      );
      onChange(promptReplacementEditorState);
      setSearchTerm("");
      setIsOpen(false);
      return;
    }

    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const nextContentState = selectionState.isCollapsed()
      ? Modifier.insertText(contentState, selectionState, emoji)
      : Modifier.replaceText(contentState, selectionState, emoji);
    const nextEditorState = EditorState.push(
      editorState,
      nextContentState,
      "insert-characters"
    );
    setFrequentEmojis(nextFrequentEmojis);
    localStorage.setItem(FREQUENT_EMOJI_KEY, JSON.stringify(nextFrequentEmojis));
    onChange(
      EditorState.forceSelection(
        nextEditorState,
        nextContentState.getSelectionAfter()
      )
    );
    setSearchTerm("");
    setIsOpen(false);
  };

  const filteredEmojis = searchTerm.trim()
    ? ALL_EMOJIS.filter((item) => {
        const query = searchTerm.trim().toLowerCase();
        return (
          item.emoji.includes(query) ||
          item.keywords.toLowerCase().includes(query) ||
          item.category.toLowerCase().includes(query)
        );
      })
    : [];
  const activeEmojiCategory = EMOJI_CATEGORIES.find(
    (category) => category.id === activeCategory
  );
  const sections = searchTerm.trim()
    ? [{ id: "search", label: "Search Results", emojis: filteredEmojis }]
    : [
        {
          id: activeEmojiCategory.id,
          label: activeEmojiCategory.label,
          emojis: activeEmojiCategory.emojis.map(([emoji]) => ({ emoji })),
        },
      ];

  return (
    <div className="custom-emoji-toolbar">
      <button
        type="button"
        className="rdw-option-wrapper custom-emoji-trigger"
        aria-label="Insert emoji"
        title="Insert emoji"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setIsOpen((current) => !current)}
      >
        🙂
      </button>
      {isOpen ? (
        <div
          className="custom-emoji-modal linkedin-emoji-picker"
          onMouseDown={(event) => {
            if (
              event.target.tagName !== "INPUT" &&
              !event.target.closest(".linkedin-emoji-sections")
            ) {
              event.preventDefault();
            }
          }}
        >
          <div className="linkedin-emoji-search-row">
            <span className="linkedin-emoji-search-icon">⌕</span>
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search"
              className="linkedin-emoji-search"
              autoFocus
            />
            <button
              type="button"
              className="linkedin-emoji-close"
              aria-label="Close emoji picker"
              onClick={() => setIsOpen(false)}
            >
              ×
            </button>
          </div>
          <div className="linkedin-emoji-tabs">
            {EMOJI_CATEGORIES.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`linkedin-emoji-tab ${
                  activeCategory === category.id ? "active" : ""
                }`}
                title={category.label}
                onClick={() => {
                  setActiveCategory(category.id);
                  setSearchTerm("");
                }}
              >
                {category.icon}
              </button>
            ))}
          </div>
          <div
            className="linkedin-emoji-sections"
            onWheel={(event) => {
              event.stopPropagation();
              event.currentTarget.scrollTop += event.deltaY;
            }}
            onMouseDown={(event) => {
              if (event.target.closest("button")) {
                event.preventDefault();
              }
            }}
          >
            {!searchTerm.trim() && frequentEmojis.length ? (
              <div className="linkedin-emoji-section">
                <div className="linkedin-emoji-section-title">
                  Frequently Used
                </div>
                <div className="linkedin-emoji-grid">
                  {frequentEmojis.map((emoji) => (
                    <button
                      key={`frequent-${emoji}`}
                      type="button"
                      className="custom-emoji-item"
                      onClick={() => insertEmoji(emoji)}
                      title={emoji}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {sections.map((section) => (
              <div key={section.id} className="linkedin-emoji-section">
                <div className="linkedin-emoji-section-title">
                  {section.label}
                </div>
                {section.emojis.length ? (
                  <div className="linkedin-emoji-grid">
                    {section.emojis.map((item) => (
                      <button
                        key={`${section.id}-${item.emoji}`}
                        type="button"
                        className="custom-emoji-item"
                        onClick={() => insertEmoji(item.emoji)}
                        title={item.emoji}
                      >
                        {item.emoji}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="linkedin-emoji-empty">No emoji found</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const NormalEditor = (
  {
    onChange,
    content = "",
    placeholder = "Type here...",
    disabled = false,
    toolbarPreset = "basic",
    minHeight = 120,
    renderBeforeEditor,
    onRequestInlineImageUpload,
    renderToolbarStart,
    renderToolbarEnd,
    // Only used by the "outline" preset's built-in image toolbar button —
    // react-draft-wysiwyg's own contract: (file) => Promise<{ data: { link: string } }>.
    // Every other preset ignores this; existing callers are unaffected.
    imageUploadCallback,
    // Suppresses this instance's own toolbar entirely (regardless of
    // preset) — for when a parent renders ONE shared/sticky toolbar for
    // several NormalEditor instances instead of one per instance. Combine
    // with onFocus/onActiveStylesChange + the ref's toggleInlineStyle/
    // insertImage to drive this editor from that external toolbar.
    hideToolbar = false,
    onFocus,
    onActiveStylesChange,
    onActiveBlockTypeChange,
  },
  forwardedRef,
) => {
  const [editorState, setEditorState] = useState(() =>
    createEditorStateFromHtml(content, toolbarPreset === "article")
  );
  const [imageEditBlockKey, setImageEditBlockKey] = useState(null);
  const [imageEditDraft, setImageEditDraft] = useState(null);
  const lastExternalContentRef = useRef(normalizeEditorHtml(content));
  const shellRef = useRef(null);
  const draftEditorRef = useRef(null);
  const articleImageHandlersRef = useRef({});
  const draggedArticleImageKeyRef = useRef(null);
  // Guards against a race where the underlying rich-text editor fires its own
  // onEditorStateChange right after we programmatically inject content (e.g.
  // from Writing Assistant), using a stale pre-injection editorState. Without
  // this, that stale echo gets re-serialized to HTML and silently overwrites
  // the just-injected full content with a shorter one — intermittently, since
  // it depends on exact render timing.
  const suppressNextCommitRef = useRef(false);
  // Debounces the expensive part of every keystroke: convertToRaw+draftToHtml
  // re-serializes the ENTIRE document (not just what changed), and the result
  // then propagates all the way up through onChange into the parent form's
  // state (postText), forcing that whole — often very large — surrounding
  // page to re-render. Doing that synchronously on every single character is
  // what caused the reported per-letter typing lag. setEditorState below
  // still runs immediately so the editor itself stays instantly responsive;
  // only the upward propagation is deferred a few ms until typing pauses.
  const commitDebounceRef = useRef(null);
  const pendingEditorStateRef = useRef(null);

  const flushPendingCommit = () => {
    if (commitDebounceRef.current == null) return null;
    clearTimeout(commitDebounceRef.current);
    commitDebounceRef.current = null;
    const stateToCommit = pendingEditorStateRef.current;
    pendingEditorStateRef.current = null;
    return stateToCommit ? serializeAndPropagate(stateToCommit) : null;
  };

  // Extracted so it can run either debounced (typing) or immediately
  // (flush) without duplicating the serialization logic. Returns the
  // computed HTML so a caller forcing a flush (see flushPendingCommit) can
  // use the fresh value immediately, rather than relying on the parent's
  // onChange -> setState -> re-render cycle to land before it reads its own
  // state back out (React state updates aren't synchronous, so a caller
  // reading its own `postText` state variable right after calling flush()
  // would otherwise still see the pre-flush value).
  const serializeAndPropagate = (safeEditorState) => {
    const rawContent = convertToRaw(safeEditorState.getCurrentContent());
    const rawContentForHtml =
      toolbarPreset === "article"
        ? prepareArticleRawContentForHtml(rawContent)
        : rawContent;
    const htmlContent = normalizeEditorHtml(
      draftToHtml(
        rawContentForHtml,
        undefined,
        undefined,
        toolbarPreset === "article" ? articleEntityTransform : undefined
      )
    );

    lastExternalContentRef.current = htmlContent;
    onChange?.(htmlContent);
    return htmlContent;
  };

  // Flush on unmount so a component removed mid-debounce (e.g. drawer closed
  // immediately after the last keystroke) doesn't drop that final character.
  useEffect(() => () => flushPendingCommit(), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Skip entirely while a debounced commit is still pending (the user is
    // actively mid-typing, e.g. pressing Enter more than once in a row).
    // `content` is this render's prop value, but the debounce/commit cycle
    // updates `lastExternalContentRef` synchronously and independently of
    // React's render timing — if a second keystroke-triggered commit lands
    // before the first one's echo has finished propagating back down as a
    // new `content` prop, this effect could see a `content` value that's
    // already one commit behind `lastExternalContentRef.current`, decide
    // (wrongly) that something changed the content externally, and reset
    // editorState to that stale, reparsed HTML — silently discarding
    // whatever the user typed in between (e.g. losing the second of two
    // blank lines typed in quick succession). Any reset is always wrong
    // while there's unflushed pending typing; once the debounce naturally
    // fires, lastExternalContentRef catches up and this effect correctly
    // no-ops on its next run.
    if (commitDebounceRef.current != null) return;

    const normalizedContent = normalizeEditorHtml(content);

    if (normalizedContent !== lastExternalContentRef.current) {
      suppressNextCommitRef.current = true;
      setEditorState(
        createEditorStateFromHtml(
          normalizedContent,
          toolbarPreset === "article"
        )
      );
      lastExternalContentRef.current = normalizedContent;
    }
  }, [content, toolbarPreset]);

  const commitEditorState = (newEditorState) => {
    if (suppressNextCommitRef.current) {
      // Ignore exactly one onChange echo following an external content
      // injection — the injected content is already correct and already
      // propagated via syncPostText/onChange upstream; re-serializing here
      // would risk overwriting it with a stale, truncated version.
      suppressNextCommitRef.current = false;
      return;
    }

    const safeEditorState =
      toolbarPreset === "article"
        ? sanitizeArticleEditorState(newEditorState)
        : newEditorState;

    // Synchronous — this is what makes the caret/typed character appear
    // instantly; it's cheap (Draft.js's own internal diffing), unlike the
    // full-document serialization below.
    setEditorState(safeEditorState);

    if (isEditorStateEmpty(safeEditorState)) {
      if (commitDebounceRef.current) {
        clearTimeout(commitDebounceRef.current);
        commitDebounceRef.current = null;
      }
      pendingEditorStateRef.current = null;
      lastExternalContentRef.current = "";
      onChange?.("");
      return;
    }

    // Deferred — see the comment above commitDebounceRef's declaration.
    pendingEditorStateRef.current = safeEditorState;
    if (commitDebounceRef.current) clearTimeout(commitDebounceRef.current);
    commitDebounceRef.current = setTimeout(() => {
      commitDebounceRef.current = null;
      const stateToCommit = pendingEditorStateRef.current;
      pendingEditorStateRef.current = null;
      if (stateToCommit) serializeAndPropagate(stateToCommit);
    }, 150);
  };

  const handleEditorChange = (newEditorState) => {
    const nextEditorState =
      toolbarPreset === "article"
        ? ensureArticleEditableBlockAfterImage(
            convertArticleWritingPromptBlocksToEmpty(
              removeArticleWritingPromptPrefix(newEditorState)
            )
          )
        : newEditorState;

    commitEditorState(nextEditorState);
  };

  const handleArticleBeforeInput = (chars, currentEditorState) => {
    if (toolbarPreset !== "article") {
      return "not-handled";
    }

    const nextEditorState = replaceArticleWritingPromptText(
      currentEditorState || editorState,
      chars
    );

    if (!nextEditorState) {
      return "not-handled";
    }

    commitEditorState(nextEditorState);
    return "handled";
  };

  // Stable array/element reference across renders — `[<EmojiToolbarButton />]`
  // inline in the JSX below built a brand-new array and element on every
  // single keystroke (since setEditorState re-renders this component every
  // time), which the underlying toolbar library sees as "the custom buttons
  // changed" on every render and re-processes accordingly. toolbarPreset is
  // fixed per editor instance, so this only actually needs to be built once.
  const linkedinToolbarCustomButtons = useMemo(
    () => (toolbarPreset === "linkedin" ? [<EmojiToolbarButton />] : undefined),
    [toolbarPreset]
  );

  // "outline" is the only preset whose toolbar config needs a per-instance
  // value spliced in (the image button's uploadCallback) — every other
  // preset's config is the static object from toolbarByPreset as-is.
  const resolvedToolbar = useMemo(() => {
    const base = toolbarByPreset[toolbarPreset] || toolbarByPreset.basic;
    if (toolbarPreset === "outline" && imageUploadCallback) {
      return { ...base, image: { ...base.image, uploadCallback: imageUploadCallback } };
    }
    return base;
  }, [toolbarPreset, imageUploadCallback]);

  const currentSelection = editorState.getSelection();
  const currentBlockType = editorState
    .getCurrentContent()
    .getBlockForKey(currentSelection.getStartKey())
    .getType();
  const currentInlineStyles = editorState.getCurrentInlineStyle();

  // Reports this editor's active inline styles (and, separately, its
  // current block type — for highlighting an external shared toolbar's
  // list buttons) up to a parent driving an external shared toolbar
  // (hideToolbar mode) — only fires while these callbacks are actually
  // provided, so instances that don't use them (every other preset/caller)
  // pay no extra cost.
  useEffect(() => {
    if (!onActiveStylesChange) return;
    onActiveStylesChange(currentInlineStyles.toArray());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState, onActiveStylesChange]);

  useEffect(() => {
    if (!onActiveBlockTypeChange) return;
    onActiveBlockTypeChange(currentBlockType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState, onActiveBlockTypeChange]);

  const editorPlaceholder =
    toolbarPreset === "article" && currentBlockType !== "unstyled"
      ? ""
      : placeholder;
  const articleRawContent =
    toolbarPreset === "article"
      ? convertToRaw(editorState.getCurrentContent())
      : null;
  const articlePlaceholderBlockKey =
    articleRawContent &&
    hasArticleImageBlock(articleRawContent) &&
    !hasArticleBodyText(articleRawContent)
      ? [...(articleRawContent.blocks || [])]
          .reverse()
          .find(
            (block) =>
              block.type !== "atomic" &&
              !block.text &&
              !block.entityRanges?.length
          )?.key || null
      : null;
  const articleBlockStyleFn = (block) =>
    block.getKey() === articlePlaceholderBlockKey
      ? "linkedin-article-empty-body-block"
      : "";

  // Give genuinely-empty blocks (real blank lines) a dedicated class so the
  // "at least one full line tall" CSS fix can target ONLY those — applying
  // it to every block (the previous blanket rule) inflated every ordinary
  // paragraph's height too whenever its natural text line was shorter than
  // that minimum, which stacked up into the "too much line spacing between
  // paragraphs" look reported after pasting short, one-sentence-per-line
  // posts.
  // A blank line built from a source line that was itself a single space
  // (a common blank-line convention, and what our own plain-text paste
  // conversion round-trips it as) parses through html-to-draftjs as a block
  // whose text is literally " " (one space), never "" — so this must check
  // the TRIMMED text, or the empty-block styling below silently never
  // applies to the very blocks it exists for.
  const basicBlockStyleFn = (block) => (block.getText().trim() === "" ? "post-empty-block" : "");

  const toggleInlineStyle = (style) => {
    commitEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlockType = (blockType) => {
    commitEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };

  const handleAddLink = () => {
    const rawUrl = window.prompt("Enter link URL", "https://");
    const trimmedUrl = rawUrl?.trim();

    if (!trimmedUrl || trimmedUrl === "https://") {
      return;
    }

    const url = /^https?:\/\//i.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl}`;
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      "LINK",
      "MUTABLE",
      { url }
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const nextEditorState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
    });

    if (currentSelection.isCollapsed()) {
      const displayText = url.replace(/^https?:\/\//i, "");
      const nextContentState = Modifier.insertText(
        contentStateWithEntity,
        currentSelection,
        displayText,
        currentInlineStyles,
        entityKey
      );
      const insertedEditorState = EditorState.push(
        nextEditorState,
        nextContentState,
        "insert-characters"
      );

      commitEditorState(
        EditorState.forceSelection(
          insertedEditorState,
          nextContentState.getSelectionAfter()
        )
      );
      return;
    }

    commitEditorState(
      RichUtils.toggleLink(nextEditorState, currentSelection, entityKey)
    );
  };

  const insertImage = (imageUrl) => {
    if (!imageUrl) {
      return;
    }

    const imageDraft = htmlToDraft(
      `<p><img src="${escapeAttribute(
        imageUrl
      )}" alt="Article image" /></p>`
    );

    if (!imageDraft?.contentBlocks?.length) {
      return;
    }

    const imageContent = ContentState.createFromBlockArray(
      imageDraft.contentBlocks,
      imageDraft.entityMap
    );
    const nextContentState = Modifier.replaceWithFragment(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      imageContent.getBlockMap()
    );
    const rawContent = convertToRaw(nextContentState);
    const writingBlock = {
      key: genKey(),
      text: "",
      type: "unstyled",
      depth: 0,
      inlineStyleRanges: [],
      entityRanges: [],
      data: {},
    };
    const nextContentWithWritingBlock = convertFromRaw({
      ...rawContent,
      blocks: [...(rawContent.blocks || []), writingBlock],
    });
    const nextEditorState = EditorState.push(
      editorState,
      nextContentWithWritingBlock,
      "insert-fragment"
    );

    commitEditorState(
      EditorState.forceSelection(
        nextEditorState,
        SelectionState.createEmpty(writingBlock.key)
      )
    );
  };

  // Exposes an imperative API so a parent can drive this editor from
  // outside it — two independent needs:
  //  - flush(): force any still-pending debounced edit to commit *before*
  //    the parent reads form values for a submit (e.g. clicking Save as
  //    Draft immediately after typing). onBlurCapture below normally
  //    handles this (focus moves to the button before its click fires),
  //    but that's indirect/easy-to-defeat; this is the reliable guarantee.
  //  - toggleInlineStyle()/toggleBlockType()/insertImage(): let a single
  //    EXTERNAL toolbar (see the "outline" preset's hideToolbar prop below)
  //    drive whichever one of several NormalEditor instances on a page is
  //    currently focused, instead of every instance rendering its own
  //    toolbar — built for the Blog Write outline builder's document-style
  //    editing panel. Every other preset/caller only ever uses flush();
  //    these three are inert unless something actually calls them.
  useImperativeHandle(forwardedRef, () => ({
    flush: flushPendingCommit,
    toggleInlineStyle,
    toggleBlockType,
    insertImage,
  }));

  const handleInsertDivider = () => {
    let contentState = editorState.getCurrentContent();
    let selectionState = currentSelection;

    if (!selectionState.isCollapsed()) {
      contentState = Modifier.removeRange(
        contentState,
        selectionState,
        "forward"
      );
      selectionState = contentState.getSelectionAfter();
    }

    let nextContentState = Modifier.splitBlock(contentState, selectionState);
    nextContentState = Modifier.setBlockType(
      nextContentState,
      nextContentState.getSelectionAfter(),
      "unstyled"
    );
    nextContentState = Modifier.insertText(
      nextContentState,
      nextContentState.getSelectionAfter(),
      ARTICLE_DIVIDER_MARKER
    );
    nextContentState = Modifier.splitBlock(
      nextContentState,
      nextContentState.getSelectionAfter()
    );
    nextContentState = Modifier.setBlockType(
      nextContentState,
      nextContentState.getSelectionAfter(),
      "unstyled"
    );
    const nextEditorState = EditorState.push(
      editorState,
      nextContentState,
      "split-block"
    );

    commitEditorState(
      EditorState.forceSelection(
        nextEditorState,
        nextContentState.getSelectionAfter()
      )
    );
  };

  const removeDividerBlock = (blockKey) => {
    const contentState = editorState.getCurrentContent();
    const block = contentState.getBlockForKey(blockKey);

    if (!block) {
      return;
    }

    const rawContent = convertToRaw(contentState);
    const nextBlocks = rawContent.blocks.filter((item) => item.key !== blockKey);
    const nextContentState = nextBlocks.length
      ? convertFromRaw({ ...rawContent, blocks: nextBlocks })
      : ContentState.createFromText("");

    commitEditorState(
      EditorState.forceSelection(
        EditorState.createWithContent(nextContentState),
        nextContentState.getSelectionAfter()
      )
    );
  };

  const mergeImageBlockData = (blockKey, nextData) => {
    const contentState = editorState.getCurrentContent();
    const block = contentState.getBlockForKey(blockKey);
    const entityKey = block ? getBlockImageEntityKey(block, contentState) : null;

    if (entityKey === null || entityKey === undefined) {
      return;
    }

    const rawContent = convertToRaw(contentState);
    const nextRawContent = {
      ...rawContent,
      blocks: rawContent.blocks.map((item) =>
        item.key === blockKey
          ? {
              ...item,
              data: {
                ...(item.data || {}),
                ...nextData,
              },
            }
          : item
      ),
    };
    const nextContentState = convertFromRaw(nextRawContent);
    const nextEditorState = EditorState.push(
      editorState,
      nextContentState,
      "change-block-data"
    );

    commitEditorState(
      EditorState.forceSelection(nextEditorState, editorState.getSelection())
    );
  };

  const toggleImageSize = (blockKey) => {
    const contentState = editorState.getCurrentContent();
    const block = contentState.getBlockForKey(blockKey);
    const entityKey = block ? getBlockImageEntityKey(block, contentState) : null;

    if (entityKey === null || entityKey === undefined) {
      return;
    }

    const imageData = getArticleImageDataForBlock(block, contentState);

    if (!imageData) {
      return;
    }

    mergeImageBlockData(blockKey, {
      size: imageData.size === "wide" ? "normal" : "wide",
    });
  };

  const alignImage = (blockKey, align) => {
    mergeImageBlockData(blockKey, { align });
  };

  const moveArticleImageBlock = (sourceKey, targetKey, placement = "before") => {
    const rawContent = convertToRaw(editorState.getCurrentContent());
    const nextRawContent = moveRawBlock(
      rawContent,
      sourceKey,
      targetKey,
      placement
    );

    if (!nextRawContent) {
      return;
    }

    const nextContentState = convertFromRaw(nextRawContent);
    const nextEditorState = EditorState.push(
      editorState,
      nextContentState,
      "move-block"
    );

    commitEditorState(
      EditorState.forceSelection(
        nextEditorState,
        SelectionState.createEmpty(sourceKey)
      )
    );
  };

  const replaceImage = (blockKey) => {
    const contentState = editorState.getCurrentContent();
    const block = contentState.getBlockForKey(blockKey);
    const entityKey = block ? getBlockImageEntityKey(block, contentState) : null;

    if (entityKey === null || entityKey === undefined) {
      return;
    }

    const imageData = getArticleImageDataForBlock(block, contentState);

    if (!imageData) {
      return;
    }

    setImageEditBlockKey(blockKey);
    setImageEditDraft({
      ratio: imageData.ratio || "original",
      rotation: Number(imageData.rotation ?? 0),
      straighten: Number(imageData.straighten ?? 0),
      zoom: Number(imageData.zoom ?? 1),
      filter: imageData.filter || "original",
      brightness: Number(imageData.brightness ?? 100),
      contrast: Number(imageData.contrast ?? 100),
      saturation: Number(imageData.saturation ?? 100),
      grayscale: Number(imageData.grayscale ?? 0),
      sepia: Number(imageData.sepia ?? 0),
    });
  };

  const closeImageEditor = () => {
    setImageEditBlockKey(null);
    setImageEditDraft(null);
  };

  const applyImageEdit = () => {
    if (!imageEditBlockKey || !imageEditDraft) {
      return;
    }

    mergeImageBlockData(imageEditBlockKey, imageEditDraft);
    closeImageEditor();
  };

  const getImageDataForBlock = (blockKey) => {
    const contentState = editorState.getCurrentContent();
    const block = blockKey ? contentState.getBlockForKey(blockKey) : null;
    const entityKey = block ? getBlockImageEntityKey(block, contentState) : null;

    if (entityKey === null || entityKey === undefined) {
      return null;
    }

    return getArticleImageDataForBlock(block, contentState);
  };

  const activeImageEditData = getImageDataForBlock(imageEditBlockKey);

  const focusLastArticleTextBlock = () => {
    const lastEditableBlockKey = getLastEditableBlockKey(
      editorState.getCurrentContent()
    );

    if (!lastEditableBlockKey) {
      return;
    }

    setEditorState(
      EditorState.forceSelection(
        editorState,
        SelectionState.createEmpty(lastEditableBlockKey)
      )
    );

    setTimeout(() => {
      draftEditorRef.current?.focus?.();
    }, 0);
  };

  const handleArticleShellMouseDown = (event) => {
    const deleteButton = event.target?.closest?.("[data-divider-delete-key]");

    if (!deleteButton) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    removeDividerBlock(deleteButton.getAttribute("data-divider-delete-key"));
  };

  const handleArticleShellClick = (event) => {
    const actionButton = event.target?.closest?.("[data-divider-delete-key]");

    if (!actionButton) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  articleImageHandlersRef.current = {
    alignImage,
    focusLastArticleTextBlock,
    mergeImageBlockData,
    moveArticleImageBlock,
    removeDividerBlock,
    replaceImage,
    toggleImageSize,
  };

  useEffect(() => {
    // Extended to "outline" (the Blog Write outline builder) — its image
    // button already inserts through this same insertImage()/
    // ArticleImageBlock path, so the hover-toolbar's actual click/drag
    // handling (delete/resize/edit/drag-to-reorder) works identically once
    // wired up here too. See the matching customBlockRenderFunc/shell
    // capture-handler extensions below and the CSS duplicated for
    // .normal-editor-shell-outline in main.css.
    if (toolbarPreset !== "article" && toolbarPreset !== "outline") {
      return undefined;
    }

    const shell = shellRef.current;

    if (!shell) {
      return undefined;
    }

    const findWithinShell = (event, selector) => {
      const target = event.target?.closest?.(selector);
      return target && shell.contains(target) ? target : null;
    };

    const getDraftBlockKeyFromTarget = (target) => {
      const imageBlock = target?.closest?.("[data-article-image-key]");

      if (imageBlock && shell.contains(imageBlock)) {
        return imageBlock.getAttribute("data-article-image-key");
      }

      const offsetNode = target?.closest?.("[data-offset-key]");
      const offsetKey = offsetNode?.getAttribute?.("data-offset-key");

      return offsetKey ? offsetKey.split("-")[0] : null;
    };

    const handleImageMouseDown = (event) => {
      const actionButton = findWithinShell(
        event,
        "[data-article-image-action]"
      );

      if (actionButton) {
        const blockKey = actionButton.getAttribute("data-article-image-key");
        const action = actionButton.getAttribute("data-article-image-action");

        event.preventDefault();
        event.stopPropagation();

        const handlers = articleImageHandlersRef.current;

        if (action === "toggle") {
          handlers.toggleImageSize?.(blockKey);
        } else if (action === "align-left") {
          handlers.alignImage?.(blockKey, "left");
        } else if (action === "align-center") {
          handlers.alignImage?.(blockKey, "center");
        } else if (action === "align-right") {
          handlers.alignImage?.(blockKey, "right");
        } else if (action === "edit") {
          handlers.replaceImage?.(blockKey);
        } else if (action === "delete") {
          handlers.removeDividerBlock?.(blockKey);
        }

        return;
      }

      if (findWithinShell(event, ".rdw-editor-main")) {
        return;
      }
    };

    const handleImageDragStart = (event) => {
      const imageBlock = findWithinShell(
        event,
        "[data-article-image-draggable]"
      );

      if (!imageBlock) {
        return;
      }

      const blockKey = imageBlock.getAttribute("data-article-image-key");

      draggedArticleImageKeyRef.current = blockKey;
      imageBlock.classList.add("linkedin-article-image-dragging");
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", blockKey);
    };

    const handleImageDragOver = (event) => {
      if (!draggedArticleImageKeyRef.current) {
        return;
      }

      const targetKey = getDraftBlockKeyFromTarget(event.target);

      if (!targetKey || targetKey === draggedArticleImageKeyRef.current) {
        return;
      }

      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    };

    const handleImageDrop = (event) => {
      const sourceKey =
        draggedArticleImageKeyRef.current ||
        event.dataTransfer.getData("text/plain");
      const targetKey = getDraftBlockKeyFromTarget(event.target);

      if (!sourceKey || !targetKey || sourceKey === targetKey) {
        return;
      }

      const targetBlock = event.target.closest(
        "[data-article-image-draggable], [data-block='true']"
      );
      const targetRect = targetBlock?.getBoundingClientRect?.();
      const placement =
        targetRect && event.clientY > targetRect.top + targetRect.height / 2
          ? "after"
          : "before";

      event.preventDefault();
      event.stopPropagation();
      articleImageHandlersRef.current.moveArticleImageBlock?.(
        sourceKey,
        targetKey,
        placement
      );
      draggedArticleImageKeyRef.current = null;
      shell
        .querySelectorAll(".linkedin-article-image-dragging")
        .forEach((node) => node.classList.remove("linkedin-article-image-dragging"));
    };

    const handleImageDragEnd = () => {
      draggedArticleImageKeyRef.current = null;
      shell
        .querySelectorAll(".linkedin-article-image-dragging")
        .forEach((node) => node.classList.remove("linkedin-article-image-dragging"));
    };

    const handleImageClick = (event) => {
      if (findWithinShell(event, "[data-article-image-action]")) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    shell.addEventListener("mousedown", handleImageMouseDown, true);
    shell.addEventListener("dragstart", handleImageDragStart, true);
    shell.addEventListener("dragover", handleImageDragOver, true);
    shell.addEventListener("drop", handleImageDrop, true);
    shell.addEventListener("dragend", handleImageDragEnd, true);
    shell.addEventListener("click", handleImageClick, true);

    return () => {
      shell.removeEventListener("mousedown", handleImageMouseDown, true);
      shell.removeEventListener("dragstart", handleImageDragStart, true);
      shell.removeEventListener("dragover", handleImageDragOver, true);
      shell.removeEventListener("drop", handleImageDrop, true);
      shell.removeEventListener("dragend", handleImageDragEnd, true);
      shell.removeEventListener("click", handleImageClick, true);
    };
  }, [toolbarPreset]);

  const renderArticleBlock = (block) => {
    if (!isArticleDividerText(block.getText())) {
      const imageEntityKey = getBlockImageEntityKey(
        block,
        editorState.getCurrentContent()
      );

      if (imageEntityKey === null || imageEntityKey === undefined) {
        return block.getType() === "atomic"
          ? {
              component: ArticleImageBlock,
              editable: false,
              props: {
                onDeleteImage: removeDividerBlock,
                onReplaceImage: replaceImage,
                onToggleImageSize: toggleImageSize,
              },
            }
          : null;
      }

      return {
        component: ArticleImageBlock,
        editable: false,
        props: {
          onDeleteImage: removeDividerBlock,
          onReplaceImage: replaceImage,
          onToggleImageSize: toggleImageSize,
        },
      };
    }

    return {
      component: ArticleDividerBlock,
      editable: false,
    };
  };

  // Pasting from Word/Outlook/Google Docs/AI-writing tools/etc. brings the
  // SOURCE's own HTML along in the clipboard (inline styles, stray empty
  // <div>/<p> wrappers used for spacing, inconsistent blank-line encoding —
  // Word's own export is the classic offender: every run wrapped in
  // `style="color: windowtext; background-color: transparent; font-size:
  // 12pt;"`). Rather than trying to sanitize every source's HTML quirks
  // after the fact, handlePlainPastedText below rebuilds straight from the
  // plain-text clipboard payload instead — one paragraph per source line,
  // guaranteeing the pasted spacing matches the source exactly (the same
  // reason a plain <textarea> paste "just works": the browser strips all
  // markup from a textarea paste, leaving only the raw text).
  const handlePlainPastedText = (text, html, pastedEditorState) => {
    if (toolbarPreset === "article") return false; // has its own handler below
    if (!text) return false; // nothing to insert — let Draft.js handle it normally

    // Build straight from the plain-text clipboard payload rather than the
    // HTML one, one paragraph per source line. This mirrors how a plain
    // <textarea> paste behaves (the browser strips all markup from a
    // textarea paste, keeping only the raw text) — which is exactly why
    // simple tools like Typegrow's formatter reproduce pasted content
    // byte-for-byte: there's no HTML layer left to carry inconsistent
    // blank-line encoding, stray empty <div>s, or Word/Docs styling cruft.
    // We trade away preserving real <b>/<a> formatting from the source in
    // exchange for guaranteed 1:1 spacing fidelity, which is what's
    // actually being pasted here in practice (AI-drafted LinkedIn posts,
    // Word/Notes exports) — plain text with literal Unicode bold/italic
    // characters already baked in, no real HTML formatting to lose.
    const htmlFromText = splitPastedTextIntoLines(text, html)
      .map((line) => `<p>${escapeHtml(line)}</p>`)
      .join("");

    const contentBlock = htmlToDraft(htmlFromText);

    if (!contentBlock?.contentBlocks?.length) return false;

    const pastedContent = ContentState.createFromBlockArray(
      contentBlock.contentBlocks,
      contentBlock.entityMap
    );
    const currentContent = pastedEditorState.getCurrentContent();
    const selection = pastedEditorState.getSelection();
    const nextContentState = Modifier.replaceWithFragment(
      currentContent,
      selection,
      pastedContent.getBlockMap()
    );
    const nextEditorState = EditorState.push(
      pastedEditorState,
      nextContentState,
      "insert-fragment"
    );

    commitEditorState(
      EditorState.forceSelection(nextEditorState, nextContentState.getSelectionAfter())
    );

    return true;
  };

  const handleArticlePastedText = (text, html, pastedEditorState) => {
    if (toolbarPreset !== "article") {
      return false;
    }

    const htmlToInsert = html
      ? normalizeArticlePasteHtml(html)
      : normalizeArticlePasteHtml(textToArticlePasteHtml(text));
    const pastedBlocks = htmlToDraft(htmlToInsert);

    if (!pastedBlocks?.contentBlocks?.length) {
      return false;
    }

    const pastedContent = ContentState.createFromBlockArray(
      pastedBlocks.contentBlocks,
      pastedBlocks.entityMap
    );
    const promptSelection =
      getArticleWritingPromptSelection(pastedEditorState);
    const nextContentState = Modifier.replaceWithFragment(
      pastedEditorState.getCurrentContent(),
      promptSelection || pastedEditorState.getSelection(),
      pastedContent.getBlockMap()
    );
    const nextEditorState = EditorState.push(
      pastedEditorState,
      nextContentState,
      "insert-fragment"
    );

    commitEditorState(
      EditorState.forceSelection(
        nextEditorState,
        nextContentState.getSelectionAfter()
      )
    );

    return true;
  };

  return (
    <div
      ref={shellRef}
      className={`normal-editor-shell normal-editor-shell-${toolbarPreset}`}
      onMouseDownCapture={
        toolbarPreset === "article" ? handleArticleShellMouseDown : undefined
      }
      onClickCapture={
        toolbarPreset === "article" ? handleArticleShellClick : undefined
      }
      // Capture phase so it fires however focus leaves any descendant of the
      // editor (the underlying library doesn't reliably forward a plain
      // onBlur prop down to its contentEditable) — guarantees the parent form
      // has fully up-to-date text before a click outside (e.g. Submit) reads it.
      onBlurCapture={flushPendingCommit}
    >
      {toolbarPreset === "article" && !disabled ? (
        <div className="linkedin-article-toolbar-shell">
          <div className="linkedin-article-toolbar-start">
            {renderToolbarStart}
          </div>

          <div className="linkedin-article-toolbar-center">
            <label className="linkedin-article-style-select">
              <select
                value={currentBlockType}
                onChange={(event) => toggleBlockType(event.target.value)}
                onMouseDown={(event) => event.stopPropagation()}
              >
                {ARTICLE_BLOCK_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span className="linkedin-article-style-caret">v</span>
            </label>

            <div className="linkedin-article-toolbar-group">
              <ArticleToolbarButton
                active={currentInlineStyles.has("BOLD")}
                label="Bold"
                onClick={() => toggleInlineStyle("BOLD")}
              >
                <BoldOutlined />
              </ArticleToolbarButton>
              <ArticleToolbarButton
                active={currentInlineStyles.has("ITALIC")}
                label="Italic"
                onClick={() => toggleInlineStyle("ITALIC")}
              >
                <ItalicOutlined />
              </ArticleToolbarButton>
            </div>

            <div className="linkedin-article-toolbar-group">
              <ArticleToolbarButton
                active={currentBlockType === "unordered-list-item"}
                label="Bulleted list"
                onClick={() => toggleBlockType("unordered-list-item")}
              >
                <UnorderedListOutlined />
              </ArticleToolbarButton>
              <ArticleToolbarButton
                active={currentBlockType === "ordered-list-item"}
                label="Numbered list"
                onClick={() => toggleBlockType("ordered-list-item")}
              >
                <OrderedListOutlined />
              </ArticleToolbarButton>
            </div>

            <div className="linkedin-article-toolbar-group">
              <ArticleToolbarButton
                active={currentBlockType === "blockquote"}
                label="Block quote"
                onClick={() => toggleBlockType("blockquote")}
              >
                <span className="linkedin-article-toolbar-quote" />
              </ArticleToolbarButton>
              <ArticleToolbarButton
                active={currentBlockType === "code-block"}
                label="Code block"
                onClick={() => toggleBlockType("code-block")}
              >
                <span className="linkedin-article-toolbar-code">{`{ }`}</span>
              </ArticleToolbarButton>
              <ArticleToolbarButton
                active={false}
                label="Divider"
                onClick={handleInsertDivider}
              >
                <span className="linkedin-article-toolbar-divider" />
              </ArticleToolbarButton>
            </div>

            <div className="linkedin-article-toolbar-group">
              <EmojiToolbarButton
                editorState={editorState}
                onChange={commitEditorState}
              />
              <ArticleToolbarButton
                active={false}
                label="Add link"
                onClick={handleAddLink}
              >
                <LinkOutlined />
              </ArticleToolbarButton>
              <ArticleToolbarButton
                active={false}
                label="Insert image"
                onClick={() => onRequestInlineImageUpload?.(insertImage)}
              >
                <PictureOutlined />
              </ArticleToolbarButton>
            </div>
          </div>

          <div className="linkedin-article-toolbar-end">
            {renderToolbarEnd}
          </div>
        </div>
      ) : null}

      {renderBeforeEditor ? (
        <div className="normal-editor-before">{renderBeforeEditor}</div>
      ) : null}

      <Editor
        editorRef={(ref) => {
          draftEditorRef.current = ref;
        }}
        editorState={editorState}
        onEditorStateChange={handleEditorChange}
        onFocus={onFocus}
        placeholder={editorPlaceholder}
        readOnly={disabled}
        toolbarHidden={disabled || hideToolbar || toolbarPreset === "article"}
        toolbar={resolvedToolbar}
        toolbarCustomButtons={linkedinToolbarCustomButtons}
        customBlockRenderFunc={
          // Extended to "outline" so its inserted images render via the
          // same ArticleImageBlock hover-icon toolbar (drag/resize/edit/
          // delete) instead of react-draft-wysiwyg's own default plain
          // "L C R" alignment buttons — see the useEffect above and the
          // .normal-editor-shell-outline CSS in main.css.
          toolbarPreset === "article" || toolbarPreset === "outline"
            ? renderArticleBlock
            : undefined
        }
        customDecorators={
          toolbarPreset === "article" ? articleDecorators : undefined
        }
        blockStyleFn={
          toolbarPreset === "article" ? articleBlockStyleFn : basicBlockStyleFn
        }
        handlePastedText={
          toolbarPreset === "article" ? handleArticlePastedText : handlePlainPastedText
        }
        handleBeforeInput={
          toolbarPreset === "article" ? handleArticleBeforeInput : undefined
        }
        // Our own handlePastedText above (both branches) already sanitizes
        // pasted HTML, so this only matters as react-draft-wysiwyg's own
        // fallback for the rare paste with no HTML clipboard payload at
        // all (plain-text-only paste) — nothing to strip in that case
        // either way.
        stripPastedStyles={false}
        toolbarClassName="custom-toolbar"
        wrapperClassName="custom-wrapper"
        editorClassName="custom-editor"
        editorStyle={{
          minHeight,
          padding: 8,
        }}
      />
      {toolbarPreset === "article" && activeImageEditData ? (
        <ArticleImageEditorModal
          draft={imageEditDraft}
          imageUrl={activeImageEditData.src}
          onApply={applyImageEdit}
          onChange={(nextDraft) =>
            setImageEditDraft((currentDraft) => ({
              ...(currentDraft || {}),
              ...nextDraft,
            }))
          }
          onClose={closeImageEditor}
        />
      ) : null}
    </div>
  );
};

// Memoized so the parent form re-rendering for reasons that have nothing to
// do with this editor (another field changing, a ticking status badge, etc.)
// doesn't force a full re-render of the whole Draft.js editor + toolbar tree
// on top of the per-keystroke re-render it already does for its own typing.
export default React.memo(forwardRef(NormalEditor));
